/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/react';
import { Link } from 'react-router-dom';
import { ReactComponent as MenuOpenIcon } from 'icons/menu_open.svg';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { useAuth } from 'components/provider/Auth';
import { useBreadcrumbs } from 'components/provider/Breadcrumbs';
import { useUi } from 'components/provider/Ui';
import Breadcrumb from 'components/shared/Breadcrumb';

import Util from 'utilities';

const Header = ({ isPhone }) => {
  const { account } = useAuth();
  const { breadcrumbs } = useBreadcrumbs();
  const { toggleSidebar } = useUi();

  // Const to indicate our data source: local, staging, production, or unknown.
  // The latter is important because it allows us to fail safely if we don't know what data source we're using.
  // Better than assuming the data source and potentially deleting real data.
  // Requires the production and staging api urls to be set in .env.
  // Then it checks for a port in the api url to determine if it's local. If it doesn't match any of those, it returns unknown.
  // We can't just check for localhost because the local api url can be configured to use a different hostname.
  // If either the production or staging api urls are not set, it will return unknown as well.
  const apiUrl = import.meta.env.VITE_HELLO_API_URL;
  const prodUrl = import.meta.env.VITE_HELLO_PRODUCTION_API_URL;
  const stagingUrl = import.meta.env.VITE_HELLO_STAGING_API_URL;

  const apiDataSource = (() => {
    if (!apiUrl || !prodUrl || !stagingUrl) return 'unknown';
    if (apiUrl.includes(prodUrl)) return 'production';
    if (apiUrl.includes(stagingUrl)) return 'staging';
    try {
      if (new URL(apiUrl).port !== '') return 'local';
    } catch {
      return 'unknown';
    }
    return 'unknown';
  })();

  return (
    <header
      style={{
        display: 'grid',
        gridTemplateColumns: '80px 1fr 80px',
        gridTemplateRows: 'auto',
        gridTemplateAreas: '"left center right"',
        position: 'absolute',
        top: 0,
        left: 0,
        transition: 'transform ease-in-out 0.5s',
        transitionDelay: account ? '0.7s' : '0s',
        transform: account ? 'translateY(0px)' : 'translateY(-80px)',
        width: '100%',
        height: '65px',
        backgroundColor: '#fff',
        zIndex: '10000',
        boxShadow: '0px 1px 5px rgba(0,0,0,0.1)',
        borderBottom: '1px solid #e6e6e6'
      }}
    >
      {/* The failure mode here is to NOT show the warning so that this warning doesn't accidentally show in production. */}
      {/* Boolean(apiUrl) && Boolean(prodUrl) prevents the warning from showing if we can't determine the data source. */}
      {/* apiDataSource !== 'production' check prevents the warning from showing if we're using production data. */}
      {/* I previously used !import.meta.env.PROD here, but that doesn't work because all test builds include it. */}
      { Boolean(apiUrl) && Boolean(prodUrl) && apiDataSource !== 'production' && (
        <div
          css={{
            position: 'absolute',
            height: '6px',
            background: 'rgb(255, 95, 21)',
            zIndex: 100000,
            left: 0,
            right: 0,
          }}
        >
          <div
            css={{
              position: 'absolute',
              right: '30px',
              width: '100px',
              background: 'rgb(255, 95, 21)',
              borderBottomLeftRadius: '6px',
              borderBottomRightRadius: '6px',
              textAlign: 'center',
              fontSize: '10px',
              fontFamily: 'monospace',
              fontWeight: 600,
              color: 'white',
              padding: '4px 0px 2px',
              textTransform: 'uppercase',
              '&:before': {
                content: '" "',
                position: 'absolute',
                top: '6px',
                left: -1,
                height: 1,
                width: 1,
                background: 'rgb(255, 95, 21)',
              },
              '&:after': {
                content: '" "',
                position: 'absolute',
                top: '6px',
                right: -1,
                height: 1,
                width: 1,
                background: 'rgb(255, 95, 21)',
              },
            }}
          >
            {apiDataSource === 'staging' ? 'Staging Data' : apiDataSource === 'local' ? 'Local Data' : 'Unknown Data'}
          </div>
        </div>
      )}
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
        onClick={toggleSidebar}
      >
        <MenuOpenIcon
          css={{
            width: '32px',
            height: '65px',
          }}
        />
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        { isPhone &&
          <img
            src="icons/page/hello-star.png"
            alt="Standard Logo"
            style={{
              display: 'block',
              height: '22px'
            }}
          />
        }
        { !isPhone &&
          <Breadcrumb>
            {breadcrumbs.map(({ path, label }) => (
              <Breadcrumb.Item key={path}>
                <Link
                  to={path}
                  style={{
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#00141E',
                    textTransform: 'uppercase'
                  }}
                >
                  {label}
                </Link>
              </Breadcrumb.Item>
            ))}
          </Breadcrumb>
        }
      </div>
      <div />
    </header>
  );
}

const mapStateToProps = state => {
  return {
    isPhone: Util.isPhone(state),
  };
};

export default withRouter(connect(mapStateToProps)(Header));
